'use client';

import { Button } from '@/components/ui/button';
import { Breadcrumb } from '@/components/common/breadcrumb';
import { menuBreadcrumbItems } from '@/config/menu-catalog';
import { ExpandableCard } from '@/components/ui/expandable-card';
import { Input } from '@/components/ui/input';
import { useChart } from '@/contexts/chart-context';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import { System } from '@/lib/api/SystemRoute';
import {
  SystemDataTransferStatusDto,
  SystemStoreDto,
} from '@/lib/api/data-contracts';
import HighchartsReact from 'highcharts-react-official';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

// API 응답 타입은 data-contracts에서 가져옴
type Store = SystemStoreDto;
type TableData = SystemDataTransferStatusDto;

export function DataTransferStatusSimple() {
  const { Highcharts, isHighchartsLoaded, isLoading, error } = useChart();
  const [searchDate, setSearchDate] = useState(new Date());
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [chartOptions, setChartOptions] = useState<any>({});
  const [isExpanded, setIsExpanded] = useState(false);
  const chartRef = useRef<any>(null);
  const { toast } = useToast();

  // 날짜 포맷팅 함수
  const formatDate = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  // 이전 날짜로 이동
  const moveToPreviousDate = () => {
    const newDate = new Date(searchDate);
    newDate.setDate(newDate.getDate() - 1);
    setSearchDate(newDate);
  };

  // 다음 날짜로 이동
  const moveToNextDate = () => {
    const newDate = new Date(searchDate);
    newDate.setDate(newDate.getDate() + 1);
    setSearchDate(newDate);
  };

  // 날짜 변경 핸들러
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = new Date(e.target.value);
    if (!isNaN(newDate.getTime())) {
      setSearchDate(newDate);
    }
  };

  // 히트맵 차트 생성
  const createChart = useCallback(
    (
      xAxisCategories: string[],
      yAxisCategories: string[],
      data: Array<[number, number, number]>
    ) => {
      if (!Highcharts) return;

      const options = {
        chart: {
          type: 'heatmap',
          height: 400,
          marginBottom: 80,
          backgroundColor: 'transparent',
        },
        title: {
          text: '',
          style: {
            display: 'none',
          },
        },
        xAxis: [
          {
            categories: xAxisCategories,
            title: {
              text: '매장',
              style: {
                color: '#374151',
                fontSize: '12px',
              },
            },
            labels: {
              style: {
                color: '#374151',
                fontSize: '10px',
              },
            },
          },
          {
            linkedTo: 0,
            opposite: true,
            categories: xAxisCategories,
            labels: {
              style: {
                color: '#374151',
                fontSize: '10px',
              },
            },
          },
        ],
        yAxis: {
          categories: yAxisCategories,
          title: {
            text: '테이블',
            style: {
              color: '#374151',
              fontSize: '12px',
            },
          },
          reversed: true,
          labels: {
            style: {
              color: '#374151',
              fontSize: '10px',
            },
          },
        },
        colorAxis: {
          min: 0,
          max: 1,
          minColor: '#ffffff',
          maxColor: '#10b981',
          stops: [
            [0, '#ffffff'],
            [0.5, '#86efac'],
            [1, '#10b981'],
          ],
        },
        legend: {
          enabled: false,
        },
        tooltip: {
          formatter: function (this: any) {
            return (
              `<b>${this.series.xAxis.categories[this.point.x]}</b><br>` +
              `<b>${this.series.yAxis.categories[this.point.y]}</b><br>` +
              `<b>${this.point.value}</b> rows`
            );
          },
        },
        series: [
          {
            type: 'heatmap',
            name: '매장별 데이터 수',
            borderWidth: 1,
            borderColor: '#e5e7eb',
            data: data,
            dataLabels: {
              enabled: true,
              color: '#000000',
              style: {
                fontSize: '9px',
                fontWeight: 'normal',
              },
            },
          },
        ],
        responsive: {
          rules: [
            {
              condition: {
                maxWidth: 800,
              },
              chartOptions: {
                yAxis: {
                  labels: {
                    formatter: function (this: any) {
                      return this.value.toString().substring(0, 1);
                    },
                  },
                },
              },
            },
          ],
        },
        credits: {
          enabled: false,
        },
      };

      setChartOptions(options);
    },
    [Highcharts]
  );

  // 차트 데이터 생성
  const generateChartData = useCallback(
    (
      tables: TableData[],
      storesData: Store[]
    ): Array<[number, number, number]> => {
      const result: Array<[number, number, number]> = [];

      storesData.forEach((store, xIndex) => {
        tables.forEach((table, yIndex) => {
          const storeData = table.aggregations?.find(
            item => item.storeCd === store.storeCode
          );
          const count = storeData?.count || 0;
          result.push([xIndex, yIndex, count]);
        });
      });

      return result;
    },
    []
  );

  // 매장 목록 API 호출
  const fetchStores = useCallback(async (): Promise<Store[]> => {
    try {
      const response = await apiClient.get('system/stores');
      const stores = (await response.json()) as System.GetStores.ResponseBody;
      return stores || [];
    } catch {
      toast({
        title: '매장 목록 조회 실패',
        description: '매장 목록을 불러오는 중 오류가 발생했습니다',
        variant: 'destructive',
      });
      // 실패 시 빈 배열 반환
      return [];
    }
  }, [toast]);

  // 자료전송현황 API 호출
  const fetchDataTransferStatus = useCallback(
    async (date: string): Promise<TableData[]> => {
      try {
        const response = await apiClient.get('system/transfer-status', {
          searchParams: {
            closedt: date.replace(/-/g, ''),
          },
        });
        const data =
          (await response.json()) as System.GetTransferStatus.ResponseBody;
        return data || [];
      } catch (error) {
        toast({
          title: '자료전송현황 조회 실패',
          description: '자료전송현황을 불러오는 중 오류가 발생했습니다',
          variant: 'destructive',
        });
        throw error;
      }
    },
    [toast]
  );

  // 데이터 로드 및 차트 생성
  const loadData = useCallback(async () => {
    if (!isHighchartsLoaded || !Highcharts) return;

    try {
      setIsDataLoading(true);
      setApiError(null);

      const searchDateStr = formatDate(searchDate);
      const [storesData, tablesData] = await Promise.all([
        fetchStores(),
        fetchDataTransferStatus(searchDateStr),
      ]);

      const chartData = generateChartData(tablesData, storesData);
      const storeNames = storesData.map(
        store => store.storeName || store.storeCode || ''
      );
      const tableNames = tablesData.map(table => table.tableName || '');

      createChart(storeNames, tableNames, chartData);
    } catch {
      toast({
        title: '데이터 로드 실패',
        description: '데이터를 불러오는 중 오류가 발생했습니다',
        variant: 'destructive',
      });
      setApiError('데이터를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setIsDataLoading(false);
    }
  }, [
    isHighchartsLoaded,
    Highcharts,
    searchDate,
    fetchStores,
    fetchDataTransferStatus,
    createChart,
    generateChartData,
  ]);

  // 전체화면 확장/축소 핸들러
  const handleExpandChange = (expanded: boolean) => {
    setIsExpanded(expanded);

    // Highcharts 차트 크기 재조정
    setTimeout(() => {
      if (chartRef.current?.chart) {
        chartRef.current.chart.reflow();
      }
    }, 100);
  };

  useEffect(() => {
    if (isHighchartsLoaded && Highcharts) {
      loadData();
    }
  }, [isHighchartsLoaded, Highcharts, loadData]);

  // 로딩 상태 표시
  if (isLoading || !isHighchartsLoaded) {
    return (
      <div className='p-6 flex items-center justify-center min-h-96'>
        <div className='text-center'>
          <div className='text-lg font-medium mb-2'>
            차트 라이브러리 로딩 중...
          </div>
          <div className='text-sm text-muted-foreground'>
            잠시만 기다려주세요
          </div>
        </div>
      </div>
    );
  }

  // 에러 상태 표시
  if (error) {
    return (
      <div className='p-6 flex items-center justify-center min-h-96'>
        <div className='text-center'>
          <div className='text-lg font-medium mb-2 text-red-600'>
            차트 로딩 실패
          </div>
          <div className='text-sm text-muted-foreground'>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <ExpandableCard
      breadcrumb={
        <Breadcrumb items={menuBreadcrumbItems('data-transfer-status')} />
      }
      onExpandChange={handleExpandChange}
    >
      {/* Date Controls */}
      <div className='flex justify-center'>
        <div className='flex items-center space-x-2'>
          <Button
            variant='outline'
            size='sm'
            onClick={moveToPreviousDate}
            className='h-9 w-9 p-0 bg-purple-600 text-white hover:bg-purple-700'
          >
            <ChevronLeft className='h-4 w-4' />
          </Button>
          <Input
            type='date'
            value={formatDate(searchDate)}
            onChange={handleDateChange}
            max={formatDate(new Date())}
            className='w-40 text-center'
          />
          <Button
            variant='outline'
            size='sm'
            onClick={moveToNextDate}
            disabled={formatDate(searchDate) >= formatDate(new Date())}
            className='h-9 w-9 p-0 bg-purple-600 text-white hover:bg-purple-700 disabled:bg-gray-400'
          >
            <ChevronRight className='h-4 w-4' />
          </Button>
        </div>
      </div>

      {/* Heatmap Chart */}
      <div
        className={`${isExpanded ? 'h-[calc(100vh-180px)]' : 'h-96'} w-full`}
      >
        {apiError ? (
          <div className='h-full flex items-center justify-center'>
            <div className='text-center'>
              <div className='text-lg font-medium mb-2 text-red-600'>
                데이터 로딩 실패
              </div>
              <div className='text-sm text-muted-foreground mb-4'>
                {apiError}
              </div>
              <Button onClick={loadData} variant='outline' size='sm'>
                다시 시도
              </Button>
            </div>
          </div>
        ) : isDataLoading ? (
          <div className='h-full flex items-center justify-center text-muted-foreground'>
            데이터 로딩 중...
          </div>
        ) : Highcharts && chartOptions.series ? (
          <HighchartsReact
            ref={chartRef}
            highcharts={Highcharts}
            options={chartOptions}
            containerProps={{ style: { height: '100%', width: '100%' } }}
          />
        ) : (
          <div className='h-full flex items-center justify-center text-muted-foreground'>
            차트 로딩 중...
          </div>
        )}
      </div>
    </ExpandableCard>
  );
}
